/**
 * NFC Service - Wraps the Web NFC API for reading/writing grid positions to NFC tags.
 *
 * NFC tag format: text record containing "grid:{row},{col}"
 */

/**
 * Checks if the Web NFC API is available in the current browser.
 * @returns {boolean}
 */
export function isNFCAvailable() {
  return 'NDEFReader' in window;
}

/**
 * Parses an NDEF message to extract a grid position.
 * Expects a text record with format "grid:{row},{col}".
 * @param {Object} message - The NDEF message from a reading event
 * @returns {{ row: number, col: number }}
 * @throws {Error} If the message format is invalid
 */
export function parseGridPosition(message) {
  if (!message || !message.records || message.records.length === 0) {
    throw new Error('Invalid NFC message: no records found');
  }

  for (const record of message.records) {
    if (record.recordType === 'text') {
      let text;
      if (typeof record.data === 'string') {
        text = record.data;
      } else {
        const decoder = new TextDecoder(record.encoding || 'utf-8');
        text = decoder.decode(record.data);
      }

      const match = text.match(/^grid:(\d+),(\d+)$/);
      if (match) {
        return {
          row: parseInt(match[1], 10),
          col: parseInt(match[2], 10)
        };
      }
    }
  }

  throw new Error('Invalid NFC message: no valid grid position found');
}

/**
 * Creates the text string for writing a grid position to an NFC tag.
 * @param {number} row - Grid row
 * @param {number} col - Grid column
 * @returns {string} Formatted string "grid:{row},{col}"
 */
export function encodeGridPosition(row, col) {
  return `grid:${row},${col}`;
}

/**
 * Reads the next scanned NFC tag and returns the parsed grid position.
 * @returns {Promise<{ row: number, col: number }>}
 */
export async function readTag() {
  if (!isNFCAvailable()) {
    throw new Error('NFC is not available on this device');
  }

  const reader = new window.NDEFReader();
  await reader.scan();

  return new Promise((resolve, reject) => {
    reader.addEventListener('reading', (event) => {
      try {
        const position = parseGridPosition(event.message);
        resolve(position);
      } catch (err) {
        reject(err);
      }
    });

    reader.addEventListener('readingerror', () => {
      reject(new Error('Failed to read NFC tag'));
    });
  });
}

/**
 * Writes a grid position to an NFC tag.
 * @param {number} row - Grid row
 * @param {number} col - Grid column
 * @returns {Promise<void>}
 */
export async function writeTag(row, col) {
  if (!isNFCAvailable()) {
    throw new Error('NFC is not available on this device');
  }

  const writer = new window.NDEFReader();
  const text = encodeGridPosition(row, col);

  await writer.write({
    records: [{ recordType: 'text', data: text }]
  });
}

/**
 * Validates that a grid position is within bounds of the grid configuration.
 * @param {number} row - Grid row
 * @param {number} col - Grid column
 * @param {{ rows: number, cols: number }} gridConfig - The grid configuration
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateGridPosition(row, col, gridConfig) {
  if (row < 0) {
    return { valid: false, error: 'Row must not be negative' };
  }
  if (col < 0) {
    return { valid: false, error: 'Column must not be negative' };
  }
  if (row >= gridConfig.rows) {
    return { valid: false, error: `Row ${row} is out of bounds (max: ${gridConfig.rows - 1})` };
  }
  if (col >= gridConfig.cols) {
    return { valid: false, error: `Column ${col} is out of bounds (max: ${gridConfig.cols - 1})` };
  }
  return { valid: true };
}
